import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'lib/utils';

const buttonVariants = cva(
  // Base: squared corners (sharp is the system), inter font, lime focus halo,
  // 2px ring offset against the page bg.
  'inline-flex items-center justify-center whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // Primary CTA — lime fill on accent-fg ink. The brand-forward action.
        default:
          'bg-[var(--accent)] text-[var(--accent-fg)] hover:bg-[var(--accent-hover)] border border-transparent',
        // Strong-secondary — ink fill on bg. Use for "Sign in", "Save", etc.
        secondary:
          'bg-[var(--text)] text-[var(--bg)] hover:bg-[color-mix(in_oklab,var(--text)_85%,var(--accent)_15%)] border border-transparent',
        // Tertiary — line border, transparent fill.
        outline:
          'border border-[var(--line-strong)] bg-transparent text-[var(--text)] hover:bg-[var(--surface-2)] hover:border-[var(--text)]',
        // Quietest — no border, hover fills surface.
        ghost:
          'bg-transparent text-[var(--text-muted)] hover:bg-[var(--surface-2)] hover:text-[var(--text)] border border-transparent',
        // Destructive — rye-red fill on white.
        destructive:
          'bg-[var(--rye-red)] text-white hover:bg-[var(--rye-red)]/90 border border-transparent',
        // Inline link.
        link: 'text-[var(--text)] underline-offset-4 hover:underline border border-transparent',
      },
      size: {
        // Body button — 12/22 padding from the spec.
        default: 'h-10 px-[22px] py-3 text-sm gap-2',
        sm: 'h-8 px-3.5 text-[13px] gap-2',
        lg: 'h-11 px-8 text-base gap-2',
        // Hero CTA — exact rye.com homepage padding (16/32, with the 1px
        // bottom optical adjustment baked in via py-[15px]).
        cta: 'h-auto px-8 pt-4 pb-[15px] text-sm gap-2.5',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
